import { useEffect, useRef } from "react";
import { Car, MapPin } from "lucide-react";
import { toast } from "sonner";
import { tNL } from "../lib/journalI18n";

export const DrivingNowBanner = ({ progress, dayLabel, onFollow, t = tNL, locale = "nl-BE" }) => {
  const fmtTime = (iso) => new Date(iso).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  const ts = progress?.position_ts;
  const isToday = !!ts && new Date(ts).toDateString() === new Date().toDateString();
  const show = !!(progress?.started && !progress.finished && progress.source === "live" && isToday);
  const fresh = show && Date.now() - new Date(ts).getTime() <= 15 * 60 * 1000;
  const seen = useRef(null);
  useEffect(() => {
    if (seen.current === null) { seen.current = show; return; }
    if (show && !seen.current) toast.success(t("drivingToast"), { duration: 8000 });
    seen.current = show;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);
  if (!show) return null;
  const eta = progress.eta;
  return (
    <div data-testid="driving-now-banner" className="relative overflow-hidden rounded-2xl bg-[#1B4332] text-white shadow-lg">
      <span className="absolute -left-10 -top-10 w-40 h-40 rounded-full bg-[#74C69D]/25 blur-2xl animate-pulse" aria-hidden />
      <div className="relative flex items-center gap-3.5 px-4 py-3">
        <span className="relative shrink-0 w-10 h-10 rounded-full bg-[#74C69D]/20 flex items-center justify-center">
          <span className="absolute inset-0 rounded-full bg-[#74C69D]/30 animate-ping" />
          <Car className="relative w-5 h-5 text-[#95D5B2]" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-heading font-extrabold text-sm leading-tight" data-testid="driving-now-title">
            {t("departed")}{dayLabel ? <span className="font-semibold text-white/85"> {dayLabel}</span> : null}
          </p>
          <p className="text-xs text-white/75 mt-0.5 truncate" data-testid="driving-now-detail">
            {fresh ? t("nowLiveSeen", { time: fmtTime(ts) }) : t("onderwegSignal", { time: fmtTime(ts) })}
            {eta?.time ? ` · ${t("arrival")}${fmtTime(eta.time)}${eta.place ? ` ${t("inWord")} ${eta.place}` : ""}` : ""}
          </p>
        </div>
        {onFollow && (
          <button data-testid="driving-now-follow" onClick={onFollow}
            className="shrink-0 rounded-full bg-white text-[#1B4332] text-xs font-bold px-4 py-2 flex items-center gap-1.5 hover:bg-[#D8F3DC] active:scale-95 transition-[transform,background-color] duration-200">
            <MapPin className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{t("followMap")}</span><span className="sm:hidden">{t("follow")}</span>
          </button>
        )}
      </div>
    </div>
  );
};
